import React, { useEffect, useState } from "react";
import QIMap from "@qinvent/map";
import mapboxgl from "mapbox-gl"; // Mapbox SDK
import "mapbox-gl/dist/mapbox-gl.css";
import { Loader } from "@googlemaps/js-api-loader"; // Google Maps Loader

export const QIMapLoader = ({
  containerId,
  className = "",
  countryConfig = {
    center: [0, 0],
    minZoom: 1,
    zoom: 1,
    maxZoom: 19,
  },
  dataFormat = {
    lat: "lat",
    lon: "lng",
    time: "recorded_on",
  },
  mapProvider = "qiMap", // "qiMap", "mapbox", or "google"
  mapboxAccessToken,
  googleMapsApiKey,
  showTraffic = true,
  setMapRef = () => {},
  children,
  mapExpand,
  dockAlign,
  selectedRowId,
  maximizeMinimize,
  mapControlsPosition = "right", // "left" or "right": side for Google Maps controls
}) => {
  const [map, setMap] = useState(null);

  useEffect(() => {
    if ((maximizeMinimize || dockAlign || mapExpand || selectedRowId) && map) {
      window.qiMapProgrammaticChange = true;

      if (mapProvider === "qiMap") {
        map.getMapReference().updateSize();
      } else if (mapProvider === "mapbox" || mapProvider === "google") {
        map.resize?.();
      }

      setTimeout(() => {
        window.qiMapProgrammaticChange = false;
      }, 200);
    }
  }, [mapExpand, map, dockAlign, selectedRowId, maximizeMinimize, mapProvider]);

  useEffect(() => {
    const handleWindowResize = () => {
      window.qiMapProgrammaticChange = true;
      setTimeout(() => {
        window.qiMapProgrammaticChange = false;
      }, 300);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    let mapInstance = null;

    const initializeMap = async () => {
      // Clean up any existing map content in the container before creating a new one.
      // This prevents duplicate map instances when React 18 StrictMode re-runs effects.
      const containerEl = document.getElementById(containerId);
      if (containerEl) {
        containerEl.innerHTML = "";
      }

      if (mapProvider === "qiMap") {
        const qiMap = new QIMap({
          containerId,
          baseLayer: ["OSM"],
          dataFormat,
          currentCountry: countryConfig,
          defaultBaseLayer: "OSM",
        });
        qiMap.createMap();

        qiMap.removeLayerSource = (layerName) => {
          const layerArr = Object.keys(qiMap.layer);
          const strVal = layerName.toString();
          const eleIndex = layerArr.indexOf(strVal);
          const layer = qiMap.layer[layerArr[eleIndex]];
          layer?.getSource()?.clear();
        };

        qiMap.resetZoomLevel = () => {
          const initialSize = qiMap.getMapReference().getSize();
          const map = qiMap.getMapReference();
          const view = map.getView();
          view.setZoom(countryConfig.zoom);
          view.centerOn(countryConfig.center, initialSize, [500, 400]);
        };

        mapInstance = qiMap;
        setMap(qiMap);
        setMapRef(qiMap);
      } else if (mapProvider === "mapbox") {
        mapboxgl.accessToken = mapboxAccessToken;

        if (!containerEl) {
          console.error(`Container with ID "${containerId}" not found.`);
          return;
        }

        const mapboxMap = new mapboxgl.Map({
          container: containerId,
          style: "mapbox://styles/mapbox/streets-v11",
          center: [...countryConfig.center].reverse(),
          zoom: countryConfig.zoom,
        });

        mapboxMap.on("load", () => {
          if (showTraffic) {
            mapboxMap.addSource("traffic", {
              type: "vector",
              url: "mapbox://mapbox.mapbox-traffic-v1",
            });

            mapboxMap.addLayer({
              id: "traffic-layer",
              type: "line",
              source: "traffic",
              "source-layer": "traffic",
              paint: {
                "line-color": [
                  "match",
                  ["get", "congestion"],
                  "low",
                  "#46b979",
                  "moderate",
                  "#f1f075",
                  "heavy",
                  "#e55e5e",
                  "severe",
                  "#ff0000",
                  "#cccccc",
                ],
                "line-width": 2,
              },
              layout: {
                "icon-image": "marker", // Default Mapbox marker icon
                "icon-size": 1,
                "text-field": ["get", "plate_number"],
                "text-size": 12,
                "text-offset": [0, -1.5],
                "text-anchor": "top",
              },
            });
          }
        });

        mapInstance = mapboxMap;
        setMap(mapboxMap);
        setMapRef(mapboxMap);
      } else if (mapProvider === "google") {
        if (!googleMapsApiKey) {
          console.error("Google Maps API key is required for Google Maps provider");
          return;
        }

        if (!containerEl) {
          console.error(`Container with ID "${containerId}" not found.`);
          return;
        }

        const loader = new Loader({
          apiKey: googleMapsApiKey,
          libraries: ["visualization"],
        });

        loader
          .load()
          .then((google) => {
            try {
              // Place all Google Maps controls on the requested side. The
              // alternative layout renders them on the left; everything else
              // keeps the default right side.
              const controlsOnLeft = mapControlsPosition === "left";
              const zoomControlPosition = controlsOnLeft
                ? google.maps.ControlPosition.LEFT_BOTTOM
                : google.maps.ControlPosition.RIGHT_BOTTOM;
              const topControlPosition = controlsOnLeft
                ? google.maps.ControlPosition.LEFT_TOP
                : google.maps.ControlPosition.RIGHT_TOP;

              const googleMap = new google.maps.Map(containerEl, {
                center: { lat: countryConfig.center[1], lng: countryConfig.center[0] },
                zoom: countryConfig.zoom,
                minZoom: countryConfig.minZoom,
                maxZoom: countryConfig.maxZoom,
                // Explicitly enable the +/- zoom control. Without this the buttons
                // can stay hidden depending on the map type / gesture settings.
                zoomControl: true,
                zoomControlOptions: {
                  position: zoomControlPosition,
                },
                // Keep the pan/street-view controls on the same side as zoom.
                streetViewControl: true,
                streetViewControlOptions: {
                  position: zoomControlPosition,
                },
                fullscreenControl: true,
                fullscreenControlOptions: {
                  position: topControlPosition,
                },
              });

              if (showTraffic) {
                const trafficLayer = new google.maps.TrafficLayer();
                trafficLayer.setMap(googleMap);
              }

              mapInstance = googleMap;
              setMap(googleMap);
              setMapRef(googleMap);
            } catch (error) {
              console.error("Error initializing Google Maps:", error);
            }
          })
          .catch((error) => {
            console.error("Error loading Google Maps:", error);
          });
      }
    };

    initializeMap();

    // Cleanup: dispose map instance when effect re-runs or component unmounts
    return () => {
      if (mapInstance) {
        try {
          if (mapProvider === "qiMap" && mapInstance.getMapReference) {
            const olMap = mapInstance.getMapReference();
            if (olMap && olMap.setTarget) {
              olMap.setTarget(null);
            }
          } else if (mapProvider === "mapbox" && mapInstance.remove) {
            mapInstance.remove();
          }
        } catch (e) {
          // Silently handle disposal errors
        }
      }
      // Clear the container to prevent stale DOM from accumulating
      const containerEl = document.getElementById(containerId);
      if (containerEl) {
        containerEl.innerHTML = "";
      }
      mapInstance = null;
    };
  }, [mapProvider, mapControlsPosition]);

  return (
    <main className="relative">
      <aside className="collapsable-side-panel">{children}</aside>
      <article className={`qiMap ${className}`}>
        <div id={containerId} style={{ width: "100%", height: "100%" }}></div>
      </article>
    </main>
  );
};
